import { useState, useCallback } from 'react';
import { useConfirm } from '../components/ConfirmDialog';
import { QueuePanel } from '../components/QueuePanel';
import { KPIPanel } from '../components/KPIPanel';
import { ClassifierPanel } from '../components/ClassifierPanel';
import { MeteringPanel } from '../components/MeteringPanel';
import { PhiPanel } from '../components/PhiPanel';
import { IconectPanel } from '../components/IconectPanel';
import { OneDrivePanel } from '../components/OneDrivePanel';
import { DocumentStatsPanel } from '../components/DocumentStatsPanel';
import { ClassifierErrorsPanel } from '../components/ClassifierErrorsPanel';
import { AlertsPanel } from '../components/AlertsPanel';
import { BatchesPanel } from '../components/BatchesPanel';
import { WorkersPanel } from '../components/WorkersPanel';
import { PhiPodsPanel } from '../components/PhiPodsPanel';
import { StartBatchModal } from '../components/modals/StartBatchModal';
import { FailedJobsModal } from '../components/modals/FailedJobsModal';
import { FlushQueuesModal } from '../components/modals/FlushQueuesModal';
import { ResetAllModal } from '../components/modals/ResetAllModal';
import { DeleteBatchModal } from '../components/modals/DeleteBatchModal';
import { useDashboardStats } from '../hooks/useStats';

interface DashboardPageProps {
  refreshAll: () => void;
}

export function DashboardPage({ refreshAll }: DashboardPageProps) {
  const [selectedBatch, setSelectedBatch] = useState<string | null>(null);
  const confirm = useConfirm();

  const {
    queueStats,
    batches,
    documentStats,
    classifierStats,
    classifierErrors,
    iconectStats,
    oneDriveStats,
    phiStats,
    k8sWorkers,
    k8sVllm,
    health,
    error,
    resetClassifierStats,
    resetIconectStats,
    resetOneDriveStats,
    resetPhiStats,
  } = useDashboardStats(selectedBatch);

  // Modal states
  const [showStartBatch, setShowStartBatch] = useState(false);
  const [showFailedJobs, setShowFailedJobs] = useState(false);
  const [showFlushQueues, setShowFlushQueues] = useState(false);
  const [showResetAll, setShowResetAll] = useState(false);
  const [deleteBatchName, setDeleteBatchName] = useState<string | null>(null);
  const [retryQueueName, setRetryQueueName] = useState<string | null>(null);

  // Handlers
  const handleRetryAllFailed = useCallback(() => {
    setShowFailedJobs(true);
  }, []);

  const handleViewFailed = useCallback(() => {
    setShowFailedJobs(true);
  }, []);

  const confirmReset = useCallback(
    (label: string) =>
      confirm({
        title: `Reset ${label} statistics?`,
        message: 'This clears all lifetime stats. Cannot be undone.',
        confirmLabel: 'Reset',
        danger: true,
      }),
    [confirm]
  );

  const handleResetClassifierStats = useCallback(async () => {
    if (await confirmReset('classifier')) await resetClassifierStats();
  }, [confirmReset, resetClassifierStats]);

  const handleResetIconectStats = useCallback(async () => {
    if (await confirmReset('iConect')) await resetIconectStats();
  }, [confirmReset, resetIconectStats]);

  const handleResetOneDriveStats = useCallback(async () => {
    if (await confirmReset('OneDrive')) await resetOneDriveStats();
  }, [confirmReset, resetOneDriveStats]);

  const handleResetPhiStats = useCallback(async () => {
    if (await confirmReset('Phi/vLLM')) await resetPhiStats();
  }, [confirmReset, resetPhiStats]);

  return (
    <>
      {/* Main scrollable content with bottom padding for fixed alerts */}
      <div className="max-w-[1600px] mx-auto space-y-6 pb-48">
        {/* Error Banner */}
        {error && !error.message?.includes('API key') && (
          <div className="bg-red-950/50 border border-red-900 rounded-lg p-4">
            <p className="text-red-400">
              Error loading data: {error.message || 'Unknown error'}
            </p>
          </div>
        )}

        {/* Row 1: Batches + Key Metrics stacked (1/4) + Document Stats (3/4) */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="space-y-6 self-start">
            <KPIPanel queueStats={queueStats ?? null} documentStats={documentStats ?? null} />
            <BatchesPanel
              data={batches ?? null}
              selectedBatch={selectedBatch}
              onSelectBatch={setSelectedBatch}
              onStartBatch={() => setShowStartBatch(true)}
              onDeleteBatch={setDeleteBatchName}
            />
          </div>
          <div className="lg:col-span-3">
            <DocumentStatsPanel data={documentStats ?? null} selectedBatch={selectedBatch} />
          </div>
        </div>

        {/* Row 2: Pipeline Queues (2/4) + Worker Pods (1/4) + Phi Pods (1/4) */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-2 h-full">
            <QueuePanel
              data={queueStats ?? null}
              onRetryAllFailed={handleRetryAllFailed}
              onRetryQueueFailed={setRetryQueueName}
              onFlushQueues={() => setShowFlushQueues(true)}
              onResetAll={() => setShowResetAll(true)}
            />
          </div>
          <WorkersPanel data={k8sWorkers ?? null} />
          <PhiPodsPanel data={k8sVllm ?? null} />
        </div>

        {/* Row 3: Rate Limiter Panels */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <IconectPanel data={iconectStats ?? null} onResetStats={handleResetIconectStats} />
          <OneDrivePanel data={oneDriveStats ?? null} onResetStats={handleResetOneDriveStats} />
          <ClassifierPanel data={classifierStats ?? null} onResetStats={handleResetClassifierStats} />
          <PhiPanel data={phiStats ?? null} onResetStats={handleResetPhiStats} />
        </div>

        {/* Classifier Errors - Only shows if there are errors */}
        <ClassifierErrorsPanel data={classifierErrors ?? null} />

        {/* Phase 6: Cross-org platform usage metering */}
        <MeteringPanel />
      </div>

      {/* Fixed Alerts Panel at bottom */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-gray-950/95 backdrop-blur-sm border-t border-gray-800 px-4 py-3 max-h-48 overflow-y-auto">
        <div className="max-w-[1600px] mx-auto">
          <AlertsPanel
            health={health ?? null}
            queueStats={queueStats ?? null}
            classifierStats={classifierStats ?? null}
            onRetryFailed={handleRetryAllFailed}
            onViewFailed={handleViewFailed}
          />
        </div>
      </div>

      {/* Modals */}
      <StartBatchModal
        isOpen={showStartBatch}
        onClose={() => setShowStartBatch(false)}
      />
      <FailedJobsModal
        isOpen={showFailedJobs}
        onClose={() => setShowFailedJobs(false)}
      />
      <FailedJobsModal
        isOpen={!!retryQueueName}
        onClose={() => setRetryQueueName(null)}
        queueName={retryQueueName}
      />
      <FlushQueuesModal
        isOpen={showFlushQueues}
        onClose={() => setShowFlushQueues(false)}
        onSuccess={refreshAll}
      />
      <ResetAllModal
        isOpen={showResetAll}
        onClose={() => setShowResetAll(false)}
        onSuccess={refreshAll}
      />
      {deleteBatchName && (
        <DeleteBatchModal
          isOpen={true}
          batchName={deleteBatchName}
          onClose={() => setDeleteBatchName(null)}
          onSuccess={refreshAll}
        />
      )}
    </>
  );
}
